import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef,
  type ReactNode,
} from 'react'
import { AlertTriangle, Info, ShieldAlert } from 'lucide-react'
import clsx from 'clsx'

// ── Types ────────────────────────────────────────────────────────────────────

type ConfirmVariant = 'danger' | 'warning' | 'info'

interface ConfirmOptions {
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmVariant
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

// ── Context ──────────────────────────────────────────────────────────────────

const ConfirmContext = createContext<ConfirmFn | null>(null)

/**
 * useConfirm -- returns a confirm(options) function that opens a modal dialog
 * and resolves to `true` (confirmed) or `false` (cancelled).
 *
 * Usage:
 *   const confirm = useConfirm()
 *   const ok = await confirm({ title: 'Delete?', message: '...', variant: 'danger' })
 */
export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext)
  if (!fn) {
    throw new Error('useConfirm() must be used within a <ConfirmProvider>')
  }
  return fn
}

// ── Variant config ───────────────────────────────────────────────────────────

const VARIANT_CONFIG: Record<
  ConfirmVariant,
  {
    icon: typeof AlertTriangle
    iconBg: string
    iconColor: string
    confirmBtnClass: string
  }
> = {
  danger: {
    icon: ShieldAlert,
    iconBg: 'bg-red-900/40 border-red-800',
    iconColor: 'text-red-400',
    confirmBtnClass: 'bg-red-600 hover:bg-red-500 focus:ring-red-400',
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'bg-yellow-900/40 border-yellow-800',
    iconColor: 'text-yellow-400',
    confirmBtnClass: 'bg-yellow-600 hover:bg-yellow-500 focus:ring-yellow-400',
  },
  info: {
    icon: Info,
    iconBg: 'bg-blue-900/40 border-blue-800',
    iconColor: 'text-blue-400',
    confirmBtnClass: 'bg-brand-600 hover:bg-brand-500 focus:ring-brand-400',
  },
}

// ── Dialog component ─────────────────────────────────────────────────────────

interface DialogState extends ConfirmOptions {
  resolve: (value: boolean) => void
}

function ConfirmDialogModal({
  state,
  onClose,
}: {
  state: DialogState
  onClose: (result: boolean) => void
}) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const confirmBtnRef = useRef<HTMLButtonElement>(null)
  const cancelBtnRef = useRef<HTMLButtonElement>(null)

  const variant = state.variant ?? 'info'
  const config = VARIANT_CONFIG[variant]
  const Icon = config.icon

  // Focus the cancel button on open (safer default for destructive actions)
  useEffect(() => {
    const target = variant === 'danger' ? cancelBtnRef.current : confirmBtnRef.current
    target?.focus()
  }, [variant])

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose(false)
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  // Focus trap -- keep Tab cycling within the dialog
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleTrap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleTrap)
    return () => document.removeEventListener('keydown', handleTrap)
  }, [])

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      aria-hidden="true"
      onClick={() => onClose(false)}
    >
      {/* Dialog */}
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-message"
        className="relative bg-surface-card border border-surface-border rounded-xl shadow-2xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div
          className={clsx(
            'flex items-center justify-center w-12 h-12 rounded-full border mb-4 mx-auto',
            config.iconBg,
          )}
        >
          <Icon className={clsx('w-6 h-6', config.iconColor)} aria-hidden="true" />
        </div>

        {/* Title */}
        <h2
          id="confirm-title"
          className="text-lg font-semibold text-white text-center mb-2"
        >
          {state.title}
        </h2>

        {/* Message */}
        <p
          id="confirm-message"
          className="text-sm text-slate-400 text-center mb-6"
        >
          {state.message}
        </p>

        {/* Actions */}
        <div className="flex gap-3 justify-center">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={() => onClose(false)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-surface-card"
          >
            {state.cancelLabel ?? 'Cancel'}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={() => onClose(true)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-card',
              config.confirmBtnClass,
            )}
          >
            {state.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Provider ─────────────────────────────────────────────────────────────────

/**
 * ConfirmProvider -- wrap your app with this component.
 * Any descendant can call `const confirm = useConfirm()` and then
 * `await confirm({ title, message, variant })`.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState | null>(null)

  const confirm: ConfirmFn = useCallback((options) => {
    return new Promise<boolean>((resolve) => {
      setState({ ...options, resolve })
    })
  }, [])

  const handleClose = useCallback(
    (result: boolean) => {
      state?.resolve(result)
      setState(null)
    },
    [state],
  )

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && <ConfirmDialogModal state={state} onClose={handleClose} />}
    </ConfirmContext.Provider>
  )
}
